// SignatureDialog.jsx
import React, { useEffect, useState, useRef } from 'react';
import SignaturePad from 'react-signature-canvas';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import UploadIcon from '@mui/icons-material/Upload';
import SaveIcon from '@mui/icons-material/Save'; // Import SaveIcon
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import html2canvas from 'html2canvas';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    Select,
    MenuItem,
    Button, // Import Button from MUI
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import ClientService from '../../../Services/client.service';
import { ToastContainer, toast } from 'react-toastify';

const ProfileSignatureDialog = ({
    open,
    onClose,
    onSave,
    existingSignature,
    existingProfilePicture,
    token,
    accountantId,
    caseId
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir()
    const [signaturePreview, setSignaturePreview] = useState('');
    const [textSignatureOption, setTextSignatureOption] = useState(false);
    const [profilePreview, setProfilePreview] = useState('');
    const [sig, setSig] = useState('');

    const [textSignature, setTextSignature] = useState('');
    const [selectedFont, setSelectedFont] = useState('Arial');

    const fonts = [
        'Arial', 'Courier New', 'Georgia', 'Times New Roman', 'Verdana', 'Pacifico', 'Dancing Script'
    ];

    useEffect(() => {
        setSignaturePreview(existingSignature);
        setProfilePreview(existingProfilePicture);
    }, [existingSignature, existingProfilePicture]);

    const [newSignatureFile, setNewSignatureFile] = useState(null);
    const [newProfileFile, setNewProfileFile] = useState(null);

    const [signatureZoomOpen, setSignatureZoomOpen] = useState(false);
    const [profileZoomOpen, setProfileZoomOpen] = useState(false);

    const handleSignatureUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const objectUrl = URL.createObjectURL(file);
            setSignaturePreview(objectUrl);
            setNewSignatureFile(file);
        }
    };

    const handleProfilePictureUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const objectUrl = URL.createObjectURL(file);
            setProfilePreview(objectUrl);
            setNewProfileFile(file);
        }
    };

    const handleSave = async () => {
        try {
            if (newSignatureFile) {
                let fileToUpload;
                if (typeof newSignatureFile === 'string' && newSignatureFile.startsWith('data:image/')) {
                    const base64Data = newSignatureFile.split(',')[1];
                    const binaryData = atob(base64Data);
                    const arrayBuffer = new Uint8Array(binaryData.length);
                    for (let i = 0; i < binaryData.length; i++) {
                        arrayBuffer[i] = binaryData.charCodeAt(i);
                    }
                    const blob = new Blob([arrayBuffer], { type: 'image/png' });
                    fileToUpload = new File([blob], 'signature.png', { type: 'image/png' });
                } else {
                    fileToUpload = newSignatureFile;
                }

                const response = await ClientService.UploadSignature(token, accountantId, caseId, fileToUpload);
                if (response.ok) {
                    toast.success(t('SignatureUploadedSuccessfully'));
                } else {
                    toast.error(t('ErrorUploadingSignatureFiles'));
                }
            }
            if (newProfileFile) {
                const response = await ClientService.UploadProfilePhoto(
                    token,
                    accountantId,
                    caseId,
                    newProfileFile
                );
                if (response.ok) {
                    toast.success(t('ProfilePhotoUploadedSuccessfully'));
                } else {
                    toast.error(t('ErrorUploadingProfileFiles'));
                }
            }
            
            // Call onSave with the updated signature and profile picture data
            if (onSave) {
                onSave({
                    signature: signaturePreview,
                    profilePicture: profilePreview
                });
            }
        } catch (error) {
            toast.error(t('ErrorUploadingFiles'));
        } finally {
            onClose();
        }
    };

    const sigPad = useRef();
    const handleClearSignature = () => {
        setNewSignatureFile(existingSignature);
        setSignaturePreview(existingSignature);
        if (sigPad.current) {
            sigPad.current.clear();
            setSig('');
        }
    };

    useEffect(() => {
        if (open && sigPad.current) {
            sigPad.current.clear();
        }
    }, [open]);

    const handleSignatureChange = () => {
        try {
            if (sigPad.current && !sigPad.current.isEmpty()) {
                const signatureDataUrl = sigPad.current
                    .getTrimmedCanvas()
                    .toDataURL('image/png');
                setSignaturePreview(signatureDataUrl);
                setSig(signatureDataUrl);
                setNewSignatureFile(signatureDataUrl);
            }
        } catch (error) {
            console.error('Error processing signature:', error);
        }
    };

    const [addSignaturePad, setAddSignaturePad] = useState(false);
    const handleAddSignatureClick = (value) => {
        setAddSignaturePad(value);
    };

    const generateTextSignatureImage = async () => {
        if (!textSignature.trim()) {
            toast.error(t('PleaseEnterTextForSignature'));
            return;
        }

        const hiddenDiv = document.createElement('div');
        hiddenDiv.innerHTML = textSignature;
        hiddenDiv.style.position = 'absolute';
        hiddenDiv.style.left = '-9999px';
        hiddenDiv.style.top = '-9999px';
        hiddenDiv.style.width = '700px';
        hiddenDiv.style.height = '200px';
        hiddenDiv.style.fontSize = '40px';
        hiddenDiv.style.color = '#000';
        hiddenDiv.style.textAlign = 'center';

        const fontMatch = textSignature.match(/font-family:([^;"]+)/);
        const appliedFont = fontMatch ? fontMatch[1].trim() : selectedFont;
        hiddenDiv.style.fontFamily = appliedFont;

        document.body.appendChild(hiddenDiv);

        try {
            const canvas = await html2canvas(hiddenDiv, { backgroundColor: null });
            const dataUrl = canvas.toDataURL('image/png');
            setSignaturePreview(dataUrl);
            setNewSignatureFile(dataUrl);
        } catch (error) {
            console.error('Error rendering text to image:', error);
        } finally {
            document.body.removeChild(hiddenDiv);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                sx={{ direction: 'rtl' }}
            >
                <DialogTitle
                    sx={{
                        direction: 'rtl',
                        textAlign: 'left',
                        position: 'relative',
                    }}
                >
                    {t('AddOrUpdateSignatureOrProfilePicture')}
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{
                        textAlign: 'right',
                        direction: 'rtl',
                    }}
                >
                    {/* Signature Upload */}
                    <div style={{ marginBottom: '16px' }}>
                        <h3 style={{ marginBottom: '8px', textAlign: 'right' }}>
                            {t('Signature')}:
                        </h3>

                        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                            {addSignaturePad && (
                                <>
                                    <SignaturePad
                                        ref={sigPad}
                                        canvasProps={{
                                            className: 'signatureCanvasContainer',
                                            style: { border: '1px solid #ccc', width: '100%', height: '150px' },
                                        }}
                                        onEnd={handleSignatureChange}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleClearSignature}
                                        sx={{
                                            fontWeight: "bold",
                                            padding: "10px 24px",
                                            fontSize: "14px",
                                            background: "linear-gradient(45deg, #FF6666 30%, #FF3333 90%)",
                                            color: "#fff",
                                            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                                            "&:hover": {
                                                background: "linear-gradient(45deg, #FF3333 30%, #CC0000 90%)",
                                            },
                                            marginLeft: '20px',
                                        }}
                                    >
                                        {t('ClearSignature')}
                                    </Button>
                                </>
                            )}
                        </div>
                        {signaturePreview ? (
                            <div style={{ marginBottom: '8px', textAlign: 'right', display: 'flex', justifyContent: 'center' }}>
                                <img
                                    src={signaturePreview}
                                    alt="signature-preview"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100px',
                                        border: '1px solid #ccc',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setSignatureZoomOpen(true)}
                                />
                            </div>
                        ) : (
                            <p style={{ textAlign: 'right' }}>
                                {t('NoSignatureUploadedYet')}
                            </p>
                        )}
                        <div className={`${direction}`} style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '12px', flexWrap: 'wrap' }}>
                            {/* Text to Signature */}
                            <Tooltip title={t('TextToSignature')}>
                                <Button
                                    variant="contained"
                                    startIcon={<TextIncreaseIcon />}
                                    onClick={() => setTextSignatureOption(!textSignatureOption)}
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: "14px",
                                        padding: "12px 26px",
                                        borderRadius: "999px",
                                        background: "linear-gradient(135deg, #4D9EFF 0%, #305AFF 100%)",
                                        color: "#fff",
                                        textTransform: "none",
                                        boxShadow: "0 6px 12px rgba(0, 122, 255, 0.25)",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            background: "linear-gradient(135deg, #305AFF 0%, #1C3DBB 100%)",
                                            boxShadow: "0 4px 10px rgba(0, 85, 255, 0.3)",
                                        },
                                    }}
                                >
                                    {t("TextToSignature")}
                                </Button>
                            </Tooltip>

                            {/* Upload Signature */}
                            <Tooltip title={t('UploadSignature')}>
                                <Button
                                    variant="contained"
                                    component="label"
                                    startIcon={<UploadIcon />}
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: "14px",
                                        padding: "12px 26px",
                                        borderRadius: "999px",
                                        background: "linear-gradient(135deg, #4D9EFF 0%, #305AFF 100%)",
                                        color: "#fff",
                                        textTransform: "none",
                                        boxShadow: "0 6px 12px rgba(0, 122, 255, 0.25)",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            background: "linear-gradient(135deg, #305AFF 0%, #1C3DBB 100%)",
                                            boxShadow: "0 4px 10px rgba(0, 85, 255, 0.3)",
                                        },
                                    }}
                                >
                                    {t("UploadSignature")}
                                    <input
                                        type="file"
                                        hidden
                                        onChange={handleSignatureUpload}
                                    />
                                </Button>
                            </Tooltip>

                            {/* Draw Signature */}
                            <Tooltip title={t('ToSign')}>
                                <Button
                                    variant="contained"
                                    startIcon={<DesignServicesIcon />}
                                    onClick={() => handleAddSignatureClick(!addSignaturePad)}
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: "14px",
                                        padding: "12px 26px",
                                        borderRadius: "999px",
                                        background: "linear-gradient(135deg, #4D9EFF 0%, #305AFF 100%)",
                                        color: "#fff",
                                        textTransform: "none",
                                        boxShadow: "0 6px 12px rgba(0, 122, 255, 0.25)",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            background: "linear-gradient(135deg, #305AFF 0%, #1C3DBB 100%)",
                                            boxShadow: "0 4px 10px rgba(0, 85, 255, 0.3)",
                                        },
                                    }}
                                >
                                    {t("ToSign")}
                                </Button>
                            </Tooltip>
                        </div>

                        {textSignatureOption && (
                            <div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', height: '200px' }}>
                                    <ReactQuill
                                        theme="snow"
                                        value={textSignature}
                                        onChange={setTextSignature}
                                        formats={[
                                            'bold', 'italic', 'underline', 'strike', 'blockquote',
                                            'list', 'bullet', 'indent', 'direction', 'align', 'link',
                                            'font'
                                        ]}
                                        modules={{
                                            toolbar: [
                                                ['bold', 'italic', 'underline', 'strike'],
                                                [{ 'align': [] }],
                                            ],
                                        }}
                                        className="myCustomQuill"
                                        style={{ height: "50%", margin: "10px 0 10px 0", width: "100%" }}
                                    />
                                </div>
                                <Button
                                    variant="contained"
                                    onClick={generateTextSignatureImage}
                                    sx={{
                                        fontWeight: "bold",
                                        padding: "10px 24px",
                                        fontSize: "14px",
                                        background: "linear-gradient(45deg, #007AFF 30%, #0051D2 90%)",
                                        color: "#fff",
                                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                                        "&:hover": {
                                            background: "linear-gradient(45deg, #0051D2 30%, #003D91 90%)",
                                        },
                                        marginBottom: '10px',
                                    }}
                                >
                                    {t('GenerateTextSignature')}
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Profile Picture Upload */}
                    <div style={{ marginBottom: '16px' }}>
                        <h3 style={{ marginBottom: '8px', textAlign: 'right' }}>
                            {t('ProfilePicture')}
                        </h3>
                        {profilePreview ? (
                            <div style={{ marginBottom: '8px', textAlign: 'right' }}>
                                <img
                                    src={profilePreview}
                                    alt="profile-preview"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100px',
                                        border: '1px solid #ccc',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setProfileZoomOpen(true)}
                                />
                            </div>
                        ) : (
                            <p style={{ textAlign: 'right' }}>
                                {t('NoProfilePictureUploadedYet')}
                            </p>
                        )}
                        <div className={`${direction}`} style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '16px' }}>
                            <Button
                                variant="contained"
                                component="label"
                                startIcon={<UploadIcon />}
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "15px",
                                    padding: "12px 28px",
                                    borderRadius: "999px",
                                    background: "linear-gradient(135deg, #4D9EFF 0%, #305AFF 100%)",
                                    color: "#fff",
                                    textTransform: "none",
                                    boxShadow: "0 8px 16px rgba(0, 122, 255, 0.3)",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        background: "linear-gradient(135deg, #305AFF 0%, #1C3DBB 100%)",
                                        boxShadow: "0 6px 12px rgba(0, 85, 255, 0.35)",
                                    },
                                }}
                            >
                                {t('UploadProfilePicture')}
                                <input
                                    type="file"
                                    hidden
                                    onChange={handleProfilePictureUpload}
                                />
                            </Button>
                        </div>

                    </div>
                </DialogContent>

                <div className={`${direction}`} style={{ display: 'flex', justifyContent: 'center', margin: '16px' }}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        startIcon={<SaveIcon />}
                        sx={{
                            fontWeight: 600,
                            padding: "12px 32px",
                            fontSize: "16px",
                            borderRadius: "999px",
                            background: "linear-gradient(135deg, #34a0ff 0%, #1d72e8 100%)",
                            color: "#fff",
                            textTransform: "none",
                            boxShadow: "0 6px 12px rgba(0, 122, 255, 0.25)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                                background: "linear-gradient(135deg, #1d72e8 0%, #1256c4 100%)",
                                boxShadow: "0 4px 10px rgba(0, 85, 255, 0.3)",
                            },
                        }}
                    >
                        {t('Save')}
                    </Button>
                </div>

            </Dialog>

            {/* Signature Zoom Dialog */}
            <Dialog
                open={signatureZoomOpen}
                onClose={() => setSignatureZoomOpen(false)}
                maxWidth="lg"
                sx={{ direction: 'rtl' }}
            >
                <DialogTitle sx={{ direction: 'rtl', display: 'flex', justifyContent: 'space-between' }}>
                    <IconButton
                        aria-label="close"
                        onClick={() => setSignatureZoomOpen(false)}
                        sx={{

                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {t('Signature')}

                </DialogTitle>
                <DialogContent dividers sx={{ textAlign: 'center' }}>
                    {signaturePreview && (
                        <img
                            src={signaturePreview}
                            alt="signature-zoom"
                            style={{ maxWidth: '90vw', maxHeight: '80vh' }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Profile Zoom Dialog */}
            <Dialog
                open={profileZoomOpen}
                onClose={() => setProfileZoomOpen(false)}
                maxWidth="lg"
                sx={{ direction: 'ltr' }}
            >
                <DialogTitle sx={{ direction: 'ltr' }}>
                    {t('ProfilePicture')}
                    <IconButton
                        aria-label="close"
                        onClick={() => setProfileZoomOpen(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ textAlign: 'center' }}>
                    {profilePreview && (
                        <img
                            src={profilePreview}
                            alt="profile-zoom"
                            style={{ maxWidth: '90vw', maxHeight: '80vh' }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ProfileSignatureDialog;
